import { supabase } from "@/integrations/supabase/client";
import { validateStaffAssignmentParams, isValidUUID } from "@/utils/validators";
import { logger } from "@/utils/logger";

/**
 * Vérifie si un Director opère dans une location spécifique
 * 
 * Cette fonction vérifie :
 * - Que le director existe
 * - Qu'il est actif (is_active = true)
 * - Qu'il est assigné à la location spécifiée
 * 
 * @param directorId - ID du director à vérifier
 * @param locationId - ID de la location
 * @returns {Promise<boolean>} true si le director opère dans ce lieu
 * 
 * @example
 * const operates = await checkDirectorOperatesInLocation(
 *   'director-uuid',
 *   'location-uuid'
 * );
 * 
 * if (operates) {
 *   console.log('Director available for this location');
 * }
 */
export const checkDirectorOperatesInLocation = async (
  directorId: string,
  locationId: string
): Promise<boolean> => {
  if (!isValidUUID(directorId) || !isValidUUID(locationId)) {
    logger.warn('Invalid UUID for director/location check', { directorId, locationId });
    return false;
  }

  try {
    logger.debug('Checking if director operates in location', { directorId, locationId });

    const { data, error } = await supabase
      .from("team_film_directors")
      .select("id")
      .eq("id", directorId)
      .eq("location_id", locationId)
      .maybeSingle();

    if (error) throw error;
    
    const operates = !!data;
    logger.debug('Director location check result', { directorId, locationId, operates });
    
    return operates;
  } catch (error) {
    logger.error('Failed to check director location', { error, directorId, locationId });
    return false;
  }
};

/**
 * Attribue un Director et/ou un Personal Brand Manager à un user
 * 
 * COMPORTEMENT :
 * - Met à jour le user avec les IDs fournis
 * - Permet d'attribuer uniquement un Director, uniquement un PBM, ou les deux
 * 
 * @param userId - ID du user dans la table users
 * @param directorId - ID optionnel du director à attribuer
 * @param personalBrandManagerId - ID optionnel du PBM à attribuer
 * 
 * @returns {Promise<{success: boolean, data?: any, error?: any}>}
 * 
 * @example
 * // Attribuer les deux
 * const result = await assignStaffToUser(
 *   'user-uuid',
 *   'director-uuid',
 *   'pbm-uuid'
 * );
 * 
 * // Attribuer uniquement un director
 * await assignStaffToUser('user-uuid', 'director-uuid');
 */
export const assignStaffToUser = async (
  userId: string,
  directorId?: string,
  personalBrandManagerId?: string,
  opsId?: string,
  videoEditorId?: string,
  monteurId?: string
) => {
  // Validation des paramètres
  const validation = validateStaffAssignmentParams({ 
    customerId: userId, // Using customerId param name for validation but it's a userId
    directorId, 
    pbmId: personalBrandManagerId 
  });

  if (!validation.valid) {
    logger.error('Invalid staff assignment parameters', { error: validation.error });
    return { success: false, error: validation.error };
  }

  // Validate opsId if provided
  if (opsId !== undefined && opsId !== null && opsId !== '' && !isValidUUID(opsId)) {
    logger.error('Invalid opsId format', { opsId });
    return { success: false, error: 'Invalid opsId format' };
  }

  // Validate videoEditorId if provided
  if (videoEditorId !== undefined && videoEditorId !== null && videoEditorId !== '' && !isValidUUID(videoEditorId)) {
    logger.error('Invalid videoEditorId format', { videoEditorId });
    return { success: false, error: 'Invalid videoEditorId format' };
  }

  // Validate monteurId if provided
  if (monteurId !== undefined && monteurId !== null && monteurId !== '' && !isValidUUID(monteurId)) {
    logger.error('Invalid monteurId format', { monteurId });
    return { success: false, error: 'Invalid monteurId format' };
  }

  try {
    logger.info('Assigning staff to user', { userId, directorId, personalBrandManagerId, opsId, videoEditorId, monteurId });

    const updates: any = {};
    
    if (directorId !== undefined) {
      updates.director_id = directorId;
    }
    
    if (personalBrandManagerId !== undefined) {
      updates.personal_brand_manager_id = personalBrandManagerId;
    }

    if (opsId !== undefined) {
      updates.ops_id = opsId || null;
    }

    if (videoEditorId !== undefined) {
      updates.video_editor_id = videoEditorId || null;
    }

    if (monteurId !== undefined) {
      updates.monteur_id = monteurId || null;
    }

    const { data, error } = await supabase
      .from("users")
      .update(updates)
      .eq("id", userId)
      .select()
      .single();

    if (error) throw error;
    
    logger.info('Staff assigned to user successfully', { userId });
    return { success: true, data };
  } catch (error) {
    logger.error('Failed to assign staff to user', { error, userId });
    return { success: false, error };
  }
};

/**
 * Récupère les informations d'un user avec son staff assigné
 * 
 * Charge le user avec ses relations :
 * - Director complet
 * - Personal Brand Manager complet
 * 
 * @param userId - ID du user
 * @returns {Promise<{success: boolean, data?: any, error?: any}>}
 * 
 * @example
 * const result = await getUserAssignments('user-uuid');
 * 
 * if (result.success && result.data) {
 *   console.log('Director:', result.data.director);
 *   console.log('PBM:', result.data.personal_brand_manager);
 * }
 */
export const getUserAssignments = async (userId: string) => {
  if (!isValidUUID(userId)) {
    logger.error('Invalid userId format', { userId });
    return { success: false, error: 'Invalid userId format' };
  }

  try {
    const { data, error } = await supabase
      .from("users")
      .select(`
        *,
        director:team_film_directors!users_director_id_fkey(*),
        personal_brand_manager:team_personal_brand_managers!users_personal_brand_manager_id_fkey(*)
      `)
      .eq("id", userId)
      .single();

    if (error) throw error;
    
    const fetchUserData = async (userId: string | null) => {
      if (!userId) return null;
      try {
        const { data: userData, error: userError } = await supabase
          .from("users")
          .select("first_name, last_name, email, avatar_url, phone, job_title")
          .eq("id", userId)
          .maybeSingle();
        
        if (userError || !userData) return null;
        return userData;
      } catch {
        return null;
      }
    };
    
    // Fetch user data for director, PBM, ops, video_editor, and monteur
    const [directorUserData, pbmUserData, opsUserData, videoEditorUserData, monteurUserData] = await Promise.all([
      data.director?.user_id ? fetchUserData(data.director.user_id) : Promise.resolve(null),
      data.personal_brand_manager?.user_id ? fetchUserData(data.personal_brand_manager.user_id) : Promise.resolve(null),
      data.ops_id ? fetchUserData(data.ops_id) : Promise.resolve(null),
      data.video_editor_id ? fetchUserData(data.video_editor_id) : Promise.resolve(null),
      data.monteur_id ? fetchUserData(data.monteur_id) : Promise.resolve(null)
    ]);
    
    const mappedData = {
      ...data,
      director: data.director ? {
        ...data.director,
        first_name: directorUserData?.first_name || null,
        last_name: directorUserData?.last_name || null,
        email: directorUserData?.email || null,
        avatar_url: directorUserData?.avatar_url || null,
        phone: directorUserData?.phone || null
      } : null,
      personal_brand_manager: data.personal_brand_manager ? {
        ...data.personal_brand_manager,
        first_name: pbmUserData?.first_name || null,
        last_name: pbmUserData?.last_name || null,
        email: pbmUserData?.email || null,
        avatar_url: pbmUserData?.avatar_url || null,
        phone: pbmUserData?.phone || null
      } : null,
      ops: opsUserData ? {
        id: data.ops_id,
        user_id: data.ops_id,
        first_name: opsUserData.first_name || null,
        last_name: opsUserData.last_name || null,
        email: opsUserData.email || null,
        avatar_url: opsUserData.avatar_url || null,
        phone: opsUserData.phone || null,
        job_title: opsUserData.job_title || null
      } : null,
      video_editor: videoEditorUserData ? {
        id: data.video_editor_id,
        user_id: data.video_editor_id,
        first_name: videoEditorUserData.first_name || null,
        last_name: videoEditorUserData.last_name || null,
        email: videoEditorUserData.email || null,
        avatar_url: videoEditorUserData.avatar_url || null,
        phone: videoEditorUserData.phone || null,
        job_title: videoEditorUserData.job_title || 'Virality Expert'
      } : null,
      monteur: monteurUserData ? {
        id: data.monteur_id,
        user_id: data.monteur_id,
        first_name: monteurUserData.first_name || null,
        last_name: monteurUserData.last_name || null,
        email: monteurUserData.email || null,
        avatar_url: monteurUserData.avatar_url || null,
        phone: monteurUserData.phone || null,
        job_title: monteurUserData.job_title || 'Monteur'
      } : null
    };
    
    return { success: true, data: mappedData };
  } catch (error) {
    logger.error('Failed to fetch user assignments', { error, userId });
    return { success: false, error };
  }
};

/**
 * Attribue l'équipe de post-production à un user
 * 
 * @param userId - ID du user
 * @param videoEditorId - ID optionnel du Virality Expert
 * @param monteurId - ID optionnel du monteur
 * 
 * @returns {Promise<{success: boolean, data?: any, error?: any}>}
 */
export const assignPostProdTeamToUser = async (
  userId: string,
  videoEditorId?: string | null,
  monteurId?: string | null
) => {
  if (!isValidUUID(userId)) {
    logger.error('Invalid userId format', { userId });
    return { success: false, error: 'Invalid userId format' };
  }

  // Validate video_editor_id if provided
  if (videoEditorId !== undefined && videoEditorId !== null && videoEditorId !== '' && !isValidUUID(videoEditorId)) {
    logger.error('Invalid videoEditorId format', { videoEditorId });
    return { success: false, error: 'Invalid videoEditorId format' };
  }

  // Validate monteur_id if provided
  if (monteurId !== undefined && monteurId !== null && monteurId !== '' && !isValidUUID(monteurId)) {
    logger.error('Invalid monteurId format', { monteurId });
    return { success: false, error: 'Invalid monteurId format' };
  }

  try {
    logger.info('Assigning post-prod team to user', { userId, videoEditorId, monteurId });

    const updates: Record<string, string | null> = {};
    
    if (videoEditorId !== undefined) {
      updates.video_editor_id = videoEditorId || null;
    }
    
    if (monteurId !== undefined) {
      updates.monteur_id = monteurId || null;
    }

    const { data, error } = await supabase
      .from("users")
      .update(updates)
      .eq("id", userId)
      .select()
      .single();

    if (error) throw error;
    
    logger.info('Post-prod team assigned to user successfully', { userId });
    return { success: true, data };
  } catch (error) {
    logger.error('Failed to assign post-prod team to user', { error, userId });
    return { success: false, error };
  }
};
